/* eslint-disable import/no-default-export */
import React from 'react';

import { NotWorkV2 } from './v2Overlays/NotWorkV2';
import { AccountTypeV2 } from './v2Overlays/AccountTypeV2';
import { WorkAndOtherV2 } from './v2Overlays/WorkAndOtherV2';
import { UrgentUpdate } from './v2Overlays/UrgentUpdate';
import { LoginWithAtlassianV2 } from './v2Overlays/LoginWithAtlassianV2';

import { Noun, Verb } from '@trello/analytics';
import { Storage } from '@trello/storage';
import { track } from './Analytics';
import { AccountWarning } from './v2Overlays/AccountWarning';
import { AtlassianConfirmation } from './v2Overlays/AtlassianConfirmation';

import { usePrepMemberForAtlassianAccountOnboardingMutation } from '@trello/graphql/generated';

const analyticsSource = 'atlassianAccountOnboardingOverlay';
const STORAGE_SESSION_KEY = 'meta-analytics-flags';
const STORAGE_SESSION_ITEM = 'atlassian-account-onboarding-flow';

export enum ScreenType {
  ACCOUNT_TYPE = 'accountType',
  NOT_WORK = 'notWork',
  ATLASSIAN_LOGIN = 'atlassianLogin',
  WORK_AND_OTHER = 'workAndOther',
  URGENT_UPDATE = 'urgentUpdate',
  ACCOUNT_WARNING = 'accountWarning',
  ACCOUNT_CONFIRMATION = 'accountConfirmation',
}

export interface Login {
  id: string;
  email: string;
}

interface AtlassianAccountOnboardingOverlayProps {
  analyticsContext: object;
  id: string;
  name: string;
  screen: ScreenType;
  aaEmail: string;
  logins: Login[];
  orgName: string;
  warningDate: string;
  warningMsg: string;
  urgent: boolean;
  updateByDate: string;
  dismiss: () => void;
  setScreen: (screenType: ScreenType) => void;
}

export const AtlassianAccountOnboardingOverlayV2: React.FunctionComponent<AtlassianAccountOnboardingOverlayProps> = ({
  analyticsContext,
  id,
  name,
  screen,
  aaEmail,
  logins,
  orgName,
  dismiss,
  setScreen,
  urgent,
  warningDate,
  warningMsg,
  updateByDate,
}) => {
  const [removeEmails] = usePrepMemberForAtlassianAccountOnboardingMutation();

  const aaLogin = logins.find(({ email }) => email === aaEmail);
  const aaLoginId = aaLogin && aaLogin.id;
  const otherLogins = logins.filter(({ email }) => email !== aaEmail);
  const nonAaLoginIds = otherLogins.map((login) => login.id);

  if (!aaLoginId) {
    return null;
  }

  //Set flag in local storage for analytics in meta repo
  if (!Storage.get(STORAGE_SESSION_KEY)) {
    Storage.set(STORAGE_SESSION_KEY, { referrer: STORAGE_SESSION_ITEM });
  }

  switch (screen) {
    case ScreenType.ACCOUNT_CONFIRMATION:
      return (
        <AtlassianConfirmation
          id={id}
          name={name}
          aaEmail={aaEmail}
          onDismiss={dismiss}
          orgName={orgName}
          onClickOk={() => {
            track({
              verb: Verb.COMPLETES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by indicating intent to remove claimed email',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingGotItButton',
            });
            dismiss();
          }}
        />
      );
    case ScreenType.ATLASSIAN_LOGIN:
      return (
        <LoginWithAtlassianV2
          aaEmail={aaEmail}
          onClickLogin={() => {
            track({
              verb: Verb.COMPLETES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by logging into atlassian account',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingLoginWithAtlassianButton',
            });
          }}
          onDismiss={dismiss}
          onClickGoBack={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking back button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingMenuBackButton',
            });
            setScreen(ScreenType.WORK_AND_OTHER);
          }}
        />
      );
    case ScreenType.WORK_AND_OTHER:
      return (
        <WorkAndOtherV2
          aaEmail={aaEmail}
          otherLogins={otherLogins}
          onDismiss={dismiss}
          orgName={orgName}
          onClickContiniue={() => {
            removeEmails({
              variables: {
                memberId: 'me',
                aaLoginId,
                nonAaLoginIds,
              },
            });
            track({
              verb: Verb.REMOVES,
              noun: Noun.EMAIL,
              method: 'via onboarding modal',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingRemoveEmailButton',
            });
            setScreen(ScreenType.ATLASSIAN_LOGIN);
          }}
          onClickGoBack={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking back button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingMenuBackButton',
            });
            setScreen(ScreenType.ACCOUNT_TYPE);
          }}
        />
      );
    case ScreenType.NOT_WORK:
      return (
        <NotWorkV2
          aaEmail={aaEmail}
          onClickRemove={() => {
            track({
              verb: Verb.COMPLETES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by indicating intent to remove claimed email',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId:
                'atlassianOnboardingGoToSettingsToRemoveEmailButton',
            });
            dismiss();
          }}
          onDismiss={dismiss}
          orgName={orgName}
          onClickGoBack={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking back button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingMenuBackButton',
            });
            setScreen(ScreenType.ACCOUNT_TYPE);
          }}
        />
      );
    case ScreenType.ACCOUNT_TYPE:
      return (
        <AccountTypeV2
          aaEmail={aaEmail}
          onDismiss={dismiss}
          orgName={orgName}
          onClickWorkAndOthers={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking the mixed use account button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingMixedUseAccountButton',
            });
            setScreen(ScreenType.WORK_AND_OTHER);
          }}
          onClickNotWork={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking the personal account button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingPersonalAccountButton',
            });
            setScreen(ScreenType.NOT_WORK);
          }}
        />
      );
    case ScreenType.URGENT_UPDATE:
      return (
        <UrgentUpdate
          aaEmail={aaEmail}
          onDismiss={dismiss}
          orgName={orgName}
          updateByDate={updateByDate}
          onClickAccountType={() => {
            track({
              verb: Verb.CHANGES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking continue button',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianAccountOnboardingUrgentContinueButton',
            });
            setScreen(ScreenType.ACCOUNT_TYPE);
          }}
        />
      );
    case ScreenType.ACCOUNT_WARNING:
      return (
        <AccountWarning
          aaEmail={aaEmail}
          onDismiss={dismiss}
          orgName={orgName}
          warningDate={warningDate}
          warningMsg={warningMsg}
          urgent={urgent}
          onClickRemove={() => {
            track({
              verb: Verb.COMPLETES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by indicating intent to remove claimed email',
              context: analyticsContext,
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId:
                'atlassianOnboardingGoToSettingsToRemoveEmailButton',
            });
            dismiss();
          }}
        />
      );
    default:
      return null;
  }
};
